"use client";

import { useRef, useState } from "react";
import { ErrorMessage, Field, FieldProps, Form, Formik } from "formik";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import type { PartnerConfig } from "@/config/partners";
import { toNumericDate } from "@/lib/partner/dates";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { contactUs, getCoutactUsLoading } from "@/lib/features/home/homeSlice";
import { PhoneField, Loader } from "@/components/ui";
import { cdmLeadFormSchema } from "@/utils/validation/CdmLeadFormSchema";
import styles from "../cdm.module.scss";

// A lead-generation enquiry form for things this microsite cannot book
// online: flights and a car rental (the hotel search above already books
// the hotel itself). The owner's ask, verbatim in substance: "add a lead
// generation form which sends us an email, with requested info. Add if
// you want to book flight, car rental. Feel free to submit this and our
// team will reach out." A later ask added to that: "also make the form
// like a CTA and with a popup" — so the page now shows a short,
// inviting block with a button (this section's own heading/copy, reused
// rather than rewritten), and the button opens the unchanged form in a
// dialog.
//
// This reuses the site's existing, already-live contact pipeline
// (POST /api/v1/contact-us via the `contactUs` thunk in homeSlice) rather
// than standing up new email infrastructure: that endpoint already stores
// the enquiry, emails the Roave admin address, confirms to the customer,
// and syncs a Brevo contact. This component only composes a
// subject/message from its own structured fields and dispatches the same
// action ContactForm.tsx uses — it does not edit ContactForm or homeSlice,
// since both are shared with the main site.
//
// Dialog: reactstrap's <Modal>, the same one already used elsewhere in
// this codebase (see src/components/booking-page/CustomerInfo.tsx). Two
// of its defaults needed an explicit override to meet this task's
// accessibility bar: `trapFocus` (default false) is turned on so focus
// can never land on the inert page behind the dialog, and `labelledBy`
// is wired to the dialog's own heading so it has an accessible name.
// Everything else (open/close on the trigger, explicit close control,
// backdrop click, Escape, focus-to-trigger on close, body scroll lock)
// is reactstrap's built-in Modal behavior, not hand-rolled here.
//
// Success/failure: `contactUs`'s own reducer already swallows transport
// errors into a normal "fulfilled" action (see homeSlice.ts) and shows a
// site-wide toast either way, so that dispatch's own promise can't be
// used to distinguish success from failure — but the *resolved payload*
// still carries the same `{ status, message }` shape the reducer itself
// branches on (postApi always resolves to the server's response body, on
// both the success and the handled-error path; see src/utils/http.ts).
// This component reads that same field to decide which state to render,
// without changing homeSlice or the dispatch call it makes.
const SERVICE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "hotel", label: "Hotel" },
  { value: "flight", label: "Flight" },
  { value: "car_rental", label: "Car rental" },
];

interface CdmLeadFormValues {
  name: string;
  email: string;
  phone: string;
  phone_number: string;
  phone_code: string;
  services: string[];
  check_in: string;
  check_out: string;
  travelers: number | string;
  notes: string;
}

// Stable, filterable subject line — kept partner-driven (not a bare
// string literal) so it still reads correctly if this route is ever
// reused for another partner, while resolving to the exact wording asked
// for today: "CDM 2026 travel enquiry".
export const buildLeadSubject = (partner: PartnerConfig): string =>
  `${partner.displayName} travel enquiry`;

// Plain-text summary of the structured answers, one labelled line each,
// so whoever reads the notification email in the Roave inbox can act on
// it without opening a database.
export const buildLeadMessage = (
  values: CdmLeadFormValues,
  partner: PartnerConfig
): string => {
  const serviceLabels = SERVICE_OPTIONS.filter((option) =>
    values.services.includes(option.value)
  ).map((option) => option.label);

  const lines = [
    `Services requested: ${serviceLabels.length > 0 ? serviceLabels.join(", ") : "(none selected)"}`,
    `Travel dates: ${toNumericDate(values.check_in)} to ${toNumericDate(values.check_out)}`,
    `Travelers: ${values.travelers}`,
    `Notes: ${values.notes?.trim() ? values.notes.trim() : "(none provided)"}`,
    "",
    `Submitted from the ${partner.displayName} partner landing page (${partner.host}).`,
  ];

  return lines.join("\n");
};

const GENERIC_SUBMIT_ERROR =
  "Something went wrong sending your enquiry. Please try again.";

export default function CdmLeadForm({ partner }: { partner: PartnerConfig }) {
  const isLoading = useAppSelector(getCoutactUsLoading);
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  // "idle" while the form is showing, "success"/"error" once a submit has
  // resolved. Kept outside Formik because it decides which whole block
  // (form vs. confirmation) the dialog body renders.
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitError, setSubmitError] = useState("");
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  const openModal = () => {
    setSubmitStatus("idle");
    setSubmitError("");
    setIsOpen(true);
  };

  // Passed to Modal's `toggle`, so it also fires on backdrop click and
  // Escape, not just this explicit call from the CTA close controls.
  const closeModal = () => setIsOpen(false);

  const initialValues: CdmLeadFormValues = {
    name: "",
    email: "",
    phone: "",
    phone_number: "",
    phone_code: "",
    services: ["hotel"],
    check_in: partner.searchDefaults.checkIn,
    check_out: partner.searchDefaults.checkOut,
    travelers: partner.searchDefaults.adults,
    notes: "",
  };

  const onSubmit = async (values: CdmLeadFormValues) => {
    const numbersOnly = values.phone_number?.replace(/\D/g, "");
    try {
      const result: any = await dispatch(
        contactUs({
          url: "v1/contact-us",
          payload: {
            name: values.name,
            email: values.email,
            phone_number: numbersOnly,
            phone_code: numbersOnly !== "" ? values.phone_code : "",
            subject: buildLeadSubject(partner),
            message: buildLeadMessage(values, partner),
          },
        })
      ).unwrap();

      if (result?.status === 200) {
        setSubmitStatus("success");
      } else {
        setSubmitStatus("error");
        setSubmitError(result?.message || GENERIC_SUBMIT_ERROR);
      }
    } catch {
      // Formik's field values are left untouched here (no resetForm), and
      // the dialog stays open — the visitor's entered data survives a
      // failure so they can just retry.
      setSubmitStatus("error");
      setSubmitError(GENERIC_SUBMIT_ERROR);
    }
  };

  return (
    <section className={styles.leadForm} aria-labelledby="cdm-lead-cta-heading">
      <div className="container">
        <div className={styles.leadFormCta}>
          <p className={styles.eyebrow}>Flights &amp; car rental</p>
          <h2 id="cdm-lead-cta-heading">Need more than a hotel?</h2>
          <p className={styles.leadFormCtaCopy}>
            Roave books your hotel above. Flights and a car rental
            aren&apos;t booked online here — tell us what you need, and our
            travel team will reach out.
          </p>
          {/* Two ways out of this block, side by side: tell us what you
              need, or go to the promoter for anything Roave does not sell.
              Tickets are theirs — this link must never read as Roave
              selling them, which is why it names their site. */}
          <div className={styles.leadFormCtaActions}>
            <button
              type="button"
              ref={triggerButtonRef}
              className={styles.leadFormCtaButton}
              onClick={openModal}
            >
              Tell us what you need
            </button>
            <a
              className={styles.leadFormCtaSecondary}
              href={partner.officialSiteUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              Tickets on the official {partner.displayName} site
              <span aria-hidden="true"> ↗</span>
              <span className={styles.srOnly}> (opens in a new tab)</span>
            </a>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        toggle={closeModal}
        labelledBy="cdm-lead-modal-heading"
        trapFocus
        scrollable
        centered
        size="lg"
        className={styles.leadFormModal}
      >
        {isLoading && <Loader show={isLoading} />}
        <ModalHeader toggle={closeModal} tag="h2">
          <span id="cdm-lead-modal-heading">Need more than a hotel?</span>
        </ModalHeader>
        <ModalBody>
          {submitStatus === "success" ? (
            <div role="status" className={styles.leadFormSuccess}>
              <p>
                Thanks — we&apos;ve got your enquiry. Our travel team
                typically responds within one business day.
              </p>
            </div>
          ) : (
            <>
              <p className={styles.leadFormIntro}>
                Flights and a car rental aren&apos;t booked online here —
                tell us what you need below, and our travel team will reach
                out. Feel free to submit this even if you&apos;re only
                exploring options.
              </p>

              <Formik
                initialValues={initialValues}
                validationSchema={cdmLeadFormSchema}
                onSubmit={onSubmit}
              >
                {({ values, errors, touched, setFieldValue, isSubmitting }) => {
                  const servicesError =
                    typeof errors.services === "string" ? errors.services : undefined;

                  return (
                    <Form className={styles.leadFormGrid} noValidate>
                      <div className={styles.leadFormField}>
                        <label htmlFor="cdm-lead-name">Name</label>
                        <Field id="cdm-lead-name" name="name" type="text" aria-describedby="cdm-lead-name-error" />
                        <div className={styles.fieldError}>
                          <ErrorMessage name="name">
                            {(msg) => (
                              <span id="cdm-lead-name-error" role="alert" className={styles.errorText}>
                                {msg}
                              </span>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className={styles.leadFormField}>
                        <label htmlFor="cdm-lead-email">Email</label>
                        <Field id="cdm-lead-email" name="email" type="email" aria-describedby="cdm-lead-email-error" />
                        <div className={styles.fieldError}>
                          <ErrorMessage name="email">
                            {(msg) => (
                              <span id="cdm-lead-email-error" role="alert" className={styles.errorText}>
                                {msg}
                              </span>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className={`${styles.leadFormField} ${styles.leadFormPhone}`}>
                        <PhoneField
                          name="phone"
                          label="Phone"
                          onValueChange={(_phone, details) => {
                            const parts = details?.inputValue?.split(" ");
                            const combined = parts?.slice(1)?.join("");
                            setFieldValue("phone_number", combined);
                            setFieldValue("phone_code", parts?.[0]);
                          }}
                        />
                        <div className={styles.fieldError}>
                          {touched.phone_number && errors.phone_number && (
                            <span role="alert" className={styles.errorText}>
                              {errors.phone_number}
                            </span>
                          )}
                        </div>
                      </div>

                      <fieldset
                        className={`${styles.leadFormField} ${styles.leadFormServices}`}
                        aria-describedby={
                          touched.services && servicesError ? "cdm-lead-services-error" : undefined
                        }
                      >
                        <legend>Which do you need?</legend>
                        <div className={styles.checkboxGroup}>
                          {SERVICE_OPTIONS.map((option) => (
                            <label
                              key={option.value}
                              htmlFor={`cdm-lead-service-${option.value}`}
                              className={styles.checkboxOption}
                            >
                              <Field
                                type="checkbox"
                                id={`cdm-lead-service-${option.value}`}
                                name="services"
                                value={option.value}
                              />
                              {option.label}
                            </label>
                          ))}
                        </div>
                        <div className={styles.fieldError}>
                          {touched.services && servicesError && (
                            <span id="cdm-lead-services-error" role="alert" className={styles.errorText}>
                              {servicesError}
                            </span>
                          )}
                        </div>
                      </fieldset>

                      <div className={styles.leadFormField}>
                        <label htmlFor="cdm-lead-check-in">Check-in</label>
                        <Field id="cdm-lead-check-in" name="check_in" type="date" aria-describedby="cdm-lead-check-in-error" />
                        <div className={styles.fieldError}>
                          <ErrorMessage name="check_in">
                            {(msg) => (
                              <span id="cdm-lead-check-in-error" role="alert" className={styles.errorText}>
                                {msg}
                              </span>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className={styles.leadFormField}>
                        <label htmlFor="cdm-lead-check-out">Check-out</label>
                        <Field id="cdm-lead-check-out" name="check_out" type="date" aria-describedby="cdm-lead-check-out-error" />
                        <div className={styles.fieldError}>
                          <ErrorMessage name="check_out">
                            {(msg) => (
                              <span id="cdm-lead-check-out-error" role="alert" className={styles.errorText}>
                                {msg}
                              </span>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className={`${styles.leadFormField} ${styles.leadFormTravelers}`}>
                        <label htmlFor="cdm-lead-travelers">Number of travellers</label>
                        <Field
                          id="cdm-lead-travelers"
                          name="travelers"
                          type="number"
                          min={1}
                          aria-describedby="cdm-lead-travelers-error"
                        />
                        <div className={styles.fieldError}>
                          <ErrorMessage name="travelers">
                            {(msg) => (
                              <span id="cdm-lead-travelers-error" role="alert" className={styles.errorText}>
                                {msg}
                              </span>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className={`${styles.leadFormField} ${styles.leadFormNotes}`}>
                        <label htmlFor="cdm-lead-notes">Anything else we should know?</label>
                        <Field
                          as="textarea"
                          id="cdm-lead-notes"
                          name="notes"
                          rows={4}
                          aria-describedby="cdm-lead-notes-error"
                        />
                        <div className={styles.fieldError}>
                          <ErrorMessage name="notes">
                            {(msg) => (
                              <span id="cdm-lead-notes-error" role="alert" className={styles.errorText}>
                                {msg}
                              </span>
                            )}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className={styles.leadFormSubmitRow}>
                        <p className={styles.leadFormExpectation}>
                          Our travel team typically responds within one business day.
                        </p>
                        <button
                          type="submit"
                          className={styles.leadFormButton}
                          disabled={isLoading || isSubmitting}
                        >
                          Send enquiry
                        </button>
                      </div>

                      {submitStatus === "error" && (
                        <p role="alert" className={`${styles.errorText} ${styles.leadFormSubmitError}`}>
                          {submitError}
                        </p>
                      )}
                    </Form>
                  );
                }}
              </Formik>
            </>
          )}
        </ModalBody>
        {submitStatus === "success" && (
          <ModalFooter>
            {/* "Done" rather than a second "Close" — the header's own
                dismiss control (aria-label "Close") already exists, and a
                dialog with two identically-named controls is confusing for
                screen reader users. */}
            <button type="button" className={styles.leadFormButton} onClick={closeModal}>
              Done
            </button>
          </ModalFooter>
        )}
      </Modal>
    </section>
  );
}
